package com.heelion.farmafreelance.agent

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.double
import kotlinx.serialization.json.int
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.math.exp
import kotlin.math.ln
import kotlin.math.sqrt

/** Intento più probabile secondo il modello, con la sua probabilità (0..1). */
data class Classificazione(val intento: String, val confidenza: Double)

/**
 * Classificatore di intenti addestrato in Python (agent/training/train.py) ed esportato in
 * JSON: TF-IDF su n-grammi di caratteri + regressione logistica multinomiale.
 *
 * L'inferenza è riscritta qui a mano (niente ONNX Runtime) così gira identica su Android,
 * JS e wasm, offline e senza costi. [normalizza] e [ngrammi] DEVONO restare identici alle
 * funzioni omonime in train.py: la parità è verificata dal test sui test_vectors esportati.
 */
class IntentClassifier(
    private val labels: List<String>,
    private val vocab: Map<String, Int>,
    private val idf: DoubleArray,
    private val coef: Array<DoubleArray>,
    private val intercept: DoubleArray,
    private val ngramMin: Int,
    private val ngramMax: Int
) {
    val intenti: List<String> get() = labels

    /** Probabilità per ogni intento, nello stesso ordine di [intenti]. */
    fun probabilita(text: String): DoubleArray {
        val counts = HashMap<Int, Int>()
        for (g in ngrammi(text, ngramMin, ngramMax)) {
            val idx = vocab[g] ?: continue
            counts[idx] = (counts[idx] ?: 0) + 1
        }
        // sublinear_tf: 1 + ln(conteggio), poi pesato per idf e normalizzato L2.
        val feats = counts.mapValues { (i, c) -> (1 + ln(c.toDouble())) * idf[i] }
        val norm = sqrt(feats.values.sumOf { it * it }).takeIf { it > 0.0 } ?: 1.0

        val logits = DoubleArray(labels.size) { k ->
            val row = coef[k]
            intercept[k] + feats.entries.sumOf { (i, v) -> row[i] * v / norm }
        }
        val max = logits.max()
        val e = DoubleArray(logits.size) { exp(logits[it] - max) }
        val somma = e.sum()
        return DoubleArray(e.size) { e[it] / somma }
    }

    fun classifica(text: String): Classificazione {
        val p = probabilita(text)
        var best = 0
        for (k in p.indices) if (p[k] > p[best]) best = k
        return Classificazione(labels[best], p[best])
    }

    companion object {
        fun fromJson(json: String): IntentClassifier {
            val root = Json.parseToJsonElement(json).jsonObject
            return IntentClassifier(
                labels = root.getValue("labels").jsonArray.map { it.jsonPrimitive.content },
                vocab = root.getValue("vocab").jsonObject.mapValues { it.value.jsonPrimitive.int },
                idf = root.getValue("idf").jsonArray.map { it.jsonPrimitive.double }.toDoubleArray(),
                coef = root.getValue("coef").jsonArray
                    .map { row -> row.jsonArray.map { it.jsonPrimitive.double }.toDoubleArray() }
                    .toTypedArray(),
                intercept = root.getValue("intercept").jsonArray.map { it.jsonPrimitive.double }.toDoubleArray(),
                ngramMin = root.getValue("ngramMin").jsonPrimitive.int,
                ngramMax = root.getValue("ngramMax").jsonPrimitive.int
            )
        }

        // Stessa decomposizione NFD + rimozione degli accenti di train.py, limitata ai caratteri
        // latini (in commonMain non c'è java.text.Normalizer): gli altri diventano spazi anche là.
        private val SENZA_ACCENTO: Map<Char, Char> = buildMap {
            "àáâãäå".forEach { put(it, 'a') }
            "èéêë".forEach { put(it, 'e') }
            "ìíîï".forEach { put(it, 'i') }
            "òóôõö".forEach { put(it, 'o') }
            "ùúûü".forEach { put(it, 'u') }
            "ýÿ".forEach { put(it, 'y') }
            put('ç', 'c')
            put('ñ', 'n')
        }

        /** Minuscolo, senza accenti, solo [a-z0-9] separati da un singolo spazio. */
        fun normalizza(text: String): String {
            val sb = StringBuilder(text.length)
            for (c0 in text.lowercase()) {
                val c = SENZA_ACCENTO[c0] ?: c0
                sb.append(if (c in 'a'..'z' || c in '0'..'9') c else ' ')
            }
            return sb.split(' ').filter { it.isNotEmpty() }.joinToString(" ")
        }

        /** N-grammi di caratteri per parola, con la parola racchiusa fra spazi. */
        internal fun ngrammi(text: String, nMin: Int, nMax: Int): List<String> {
            val out = ArrayList<String>()
            for (parola in normalizza(text).split(' ')) {
                if (parola.isEmpty()) continue
                val w = " $parola "
                for (n in nMin..nMax) {
                    for (i in 0..w.length - n) out.add(w.substring(i, i + n))
                }
            }
            return out
        }
    }
}
